import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import useSWR from "swr";
import { toast } from "sonner";
import { Plus, FlaskConical, Carrot, LayoutGrid, Eye, EyeOff, Search, FileDown, X } from "lucide-react";
import Header from "@/components/Header";
import { Card, Button } from "@/components/ui-kit";
import { listSheets, setSheetActive, downloadAllSheetsPdf } from "@/lib/api";
import { getUser } from "@/lib/auth";
import { SHEET_CATEGORIES } from "@/constants/records";

export default function SheetList() {
  const navigate = useNavigate();
  const { data: sheets, mutate } = useSWR("sheets", listSheets);
  const isAdmin = getUser()?.rol === "admin";
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState("");
  const [downloading, setDownloading] = useState(false);

  const toggleVisible = async (s) => {
    try {
      await setSheetActive(s.id, s.active === false);
      toast.success(s.active === false ? "Fitxa visible" : "Fitxa oculta");
      mutate();
    } catch { toast.error("Error canviant la visibilitat"); }
  };

  const downloadAll = async () => {
    setDownloading(true);
    try {
      await downloadAllSheetsPdf();
      toast.success("PDF amb totes les fitxes descarregat");
    } catch { toast.error("Error generant el PDF"); }
    finally { setDownloading(false); }
  };

  const q = query.trim().toLowerCase();
  const filtered = (sheets || []).filter((s) => {
    if (q && !s.name.toLowerCase().includes(q)) return false;
    if (activeCat && (s.category || "Altres") !== activeCat) return false;
    return true;
  });

  // agrupar per categoria en l'ordre fix; categories buides no es mostren
  const groups = SHEET_CATEGORIES
    .map((cat) => ({ cat, items: filtered.filter((s) => (s.category || "Altres") === cat) }))
    .filter((g) => g.items.length > 0);
  const presentCats = SHEET_CATEGORIES.filter((cat) =>
    (sheets || []).some((s) => (s.category || "Altres") === cat));

  return (
    <>
      <Header title="Producció" />
      <div className="mx-auto max-w-3xl px-4 pb-24 pt-6 sm:px-6 sm:pt-10">
        <div className="animate-fade-up flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="label-eyebrow">Fitxes</div>
            <h2 className="font-display text-3xl font-bold text-ink sm:text-4xl">Fitxes de producció</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={downloadAll} disabled={downloading} testid="pdf-all">
              <FileDown size={16} /> {downloading ? "Generant…" : "PDF totes"}
            </Button>
            <Button variant="secondary" onClick={() => navigate("/allergens")} testid="go-matrix"><LayoutGrid size={16} /> Matriu</Button>
            {isAdmin && (
              <>
                <Button variant="secondary" onClick={() => navigate("/ingredients")} testid="go-ingredients"><Carrot size={16} /> Ingredients</Button>
                <Button onClick={() => navigate("/production/new")} testid="sheet-new"><Plus size={16} /> Nova</Button>
              </>
            )}
          </div>
        </div>

        {/* Cercador + filtre de categories */}
        <div className="mt-6 animate-fade-up space-y-3">
          <div className="relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-soft" />
            <input
              type="search" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Cercar fitxa… (ex: redondo, burger, ales)" data-testid="sheet-search"
              className="h-12 w-full rounded-xl border border-line bg-white pl-11 pr-10 text-base text-ink outline-none transition-all placeholder:text-ink-soft/60 focus:border-terracotta focus:ring-2 focus:ring-terracotta/20"
            />
            {query && (
              <button onClick={() => setQuery("")} aria-label="Netejar cerca"
                className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-ink-soft hover:bg-muted">
                <X size={16} />
              </button>
            )}
          </div>
          {presentCats.length > 1 && (
            <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:px-0">
              <CatChip label="Totes" active={!activeCat} onClick={() => setActiveCat("")} />
              {presentCats.map((cat) => (
                <CatChip key={cat} label={cat} active={activeCat === cat} onClick={() => setActiveCat(activeCat === cat ? "" : cat)} />
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 space-y-7">
          {(sheets?.length === 0) && (
            <Card className="text-center text-sm text-ink-soft">
              Encara no hi ha fitxes.{isAdmin ? " Crea'n una amb \"Nova\"." : " Demana a l'administrador que en creï."}
            </Card>
          )}
          {sheets?.length > 0 && groups.length === 0 && (
            <Card className="text-center text-sm text-ink-soft">Cap fitxa coincideix amb la cerca.</Card>
          )}
          {groups.map(({ cat, items }) => (
            <section key={cat}>
              <div className="mb-2 flex items-baseline gap-2">
                <h3 className="font-display text-lg font-bold text-ink">{cat}</h3>
                <span className="text-xs text-ink-soft">{items.length}</span>
              </div>
              <div className="space-y-3">
                {items.map((s, idx) => {
                  const hidden = s.active === false;
                  return (
                    <div key={s.id} className="relative animate-fade-up" style={{ animationDelay: `${idx * 30}ms` }}>
                      <Link to={`/production/${s.id}`} data-testid={`sheet-card-${s.id}`}
                        className={"group flex items-center gap-4 rounded-2xl border border-line bg-white p-4 shadow-card transition-all hover:-translate-y-0.5 hover:shadow-soft " + (hidden ? "opacity-60" : "")}>
                        {s.photos?.length > 0 ? (
                          <img src={s.photos[0]} alt="" className="h-14 w-14 shrink-0 rounded-2xl border border-line object-cover" />
                        ) : (
                          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-terracotta text-white"><FlaskConical size={24} /></div>
                        )}
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <h4 className="font-display text-lg font-bold text-ink">{s.name}</h4>
                            {hidden && <span className="rounded-full bg-ink px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">Oculta</span>}
                          </div>
                          <div className="mt-0.5 text-sm text-ink-soft">{s.ingredients?.length || 0} ingredients</div>
                        </div>
                        <div className="pr-8 text-ink-soft transition-transform group-hover:translate-x-1">→</div>
                      </Link>
                      {isAdmin && (
                        <button onClick={() => toggleVisible(s)} title={hidden ? "Mostrar fitxa" : "Ocultar fitxa"} aria-label={hidden ? "Mostrar" : "Ocultar"}
                          className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white text-ink-soft shadow-card hover:text-ink">
                          {hidden ? <EyeOff size={17} /> : <Eye size={17} />}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </div>
    </>
  );
}

function CatChip({ label, active, onClick }) {
  return (
    <button onClick={onClick}
      className={
        "shrink-0 rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors " +
        (active ? "bg-terracotta text-white" : "bg-white text-ink-soft border border-line hover:bg-cream")
      }>
      {label}
    </button>
  );
}
